import React, { useState, useEffect, useCallback } from "react";

import api from "../../../../services/api";
import CommonSelect from "../../../../core/common/commonSelect";
import { notify } from "../../../../services/toast.config";

const STATUS_OPTIONS: any[] = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

interface EditSharedFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecord: any;
  selectedCompany: any;
  updateUI: () => Promise<void>;
}

const EditSharedFormModal: React.FC<EditSharedFormModalProps> = ({
  isOpen,
  onClose,
  selectedRecord,
  selectedCompany,
  updateUI,
}) => {
  const signature = localStorage.getItem("signature");

  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<any>({
    title: "",
    status: 1,
    regenerate_link: false,
  });
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string[];
  }>({});

  // Initialize form data when modal opens
  useEffect(() => {
    if (selectedRecord && isOpen) {
      setFormData({
        title: selectedRecord?.title || "",
        status: selectedRecord?.status || 1,
        regenerate_link: false,
      });
      setInvalidFields(new Set());
      setValidationErrors({});
    }
  }, [selectedRecord, isOpen]);

  // Validation
  const validateForm = useCallback((): boolean => {
    const errors: { [key: string]: string[] } = {};
    const invalid = new Set<string>();

    if (formData.status === undefined || formData.status === null) {
      errors["status"] = ["Status is required"];
      invalid.add("status");
    }

    setValidationErrors(errors);
    setInvalidFields(invalid);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const clearFieldError = useCallback((fieldName: string) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });

    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  // Form Handlers
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: any) => ({ ...prev, title: e.target.value }));
  };

  const handleStatusChange = (selectedStatus: any) => {
    clearFieldError("status");
    setFormData((prev: any) => ({
      ...prev,
      status: selectedStatus?.value ?? 1,
    }));
  };

  const handleRegenerateLinkChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      regenerate_link: e.target.checked,
    }));
  };

  // Modal Actions
  const handleModalClose = () => {
    setFormData({
      title: "",
      status: 1,
      regenerate_link: false,
    });
    setInvalidFields(new Set());
    setValidationErrors({});
    onClose();
  };

  // Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      notify.error("Please fix the validation errors before submitting.");
      return;
    }

    try {
      setIsSubmitting(true);

      const dataToSend = {
        signature,
        company_id: selectedCompany?.companyId,
        company_type: selectedCompany?.companytype,
        link_id: selectedRecord?.id,
        title: formData.title || null,
        status: formData.status,
        regenerate_link: formData.regenerate_link,
      };

      const response = await api.post(
        "leadService/forms/share-links/update",
        dataToSend
      );

      notify.success(
        response.data?.data?.message || "Shared form link updated successfully!"
      );

      handleModalClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        "An error occurred while updating.";
      notify.error(errorMessage);
    } finally {
      await updateUI();
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
        overflowY: "auto",
      }}
      tabIndex={-1}
      role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Edit Shared Form Link</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              onClick={handleModalClose}
              disabled={isSubmitting}
              aria-label="Close">
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body pb-0">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Form Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedRecord?.form_name || ""}
                    disabled
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Title <span className="text-muted">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter custom title for this share link"
                    value={formData.title}
                    onChange={handleTitleChange}
                    disabled={isSubmitting}
                  />
                  <small className="text-muted">
                    Custom title helps identify this specific share link
                  </small>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <div
                    className={
                      invalidFields.has("status")
                        ? "is-invalid border border-danger rounded"
                        : ""
                    }>
                    <CommonSelect
                      className="select"
                      options={STATUS_OPTIONS}
                      defaultValue={{
                        value: formData.status,
                        label:
                          STATUS_OPTIONS.find(
                            (opt) => opt.value === formData.status
                          )?.label || "Active",
                      }}
                      onChange={handleStatusChange}
                      placeHolder="Select Status"
                      isDisabled={isSubmitting}
                    />
                  </div>
                  {validationErrors["status"] && (
                    <div className="text-danger mt-1">
                      <small>{validationErrors["status"][0]}</small>
                    </div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Submission Count</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedRecord?.submission_count || 0}
                    disabled
                  />
                </div>

                <div className="col-md-12 mb-3">
                  <label className="form-label">Current Public Link</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={selectedRecord?.shareable_url || ""}
                      disabled
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          selectedRecord?.shareable_url
                        );
                        notify.success("Link copied to clipboard!");
                      }}
                      disabled={isSubmitting}>
                      <i className="ti ti-copy"></i>
                    </button>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="alert alert-warning d-flex align-items-start">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="regenerate_link"
                        checked={formData.regenerate_link}
                        onChange={handleRegenerateLinkChange}
                        disabled={isSubmitting}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="regenerate_link">
                        <strong className="text-info">
                          Regenerate Public Link
                        </strong>
                        <br />
                        <small className="text-muted">
                          Warning: This will create a new public link. The old
                          link will stop working immediately.
                        </small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleModalClose}
                disabled={isSubmitting}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Updating...
                  </>
                ) : (
                  "Update Shared Link"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditSharedFormModal;
